import { Component, Inject, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { Observable } from "rxjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { take } from "rxjs/operators";
import {
  FormHelper,
  LoanDocumentService,
  OnDemandStatementEnvelope,
  OnDemandStatementRequest,
  OnDemandStatementStatus,
  StatementDownloadResult,
  StatementFormat,
} from "common";
import {
  RequestStatementDialogData,
  StatementLoanOption,
} from "./models/request-statement-dialog-data.model";
import { StatementPeriodSelection } from "./month-year-picker/month-year-period.model";
import { LoanFacade } from "../../loan/domain/+state/loan.facade";
import { LoanData, ProductCode } from "../../loan/domain/models/loan.model";

const STATUS_FALLBACK_MESSAGES: Record<OnDemandStatementStatus, string> = {
  [OnDemandStatementStatus.NoActivity]: "No statement available for the selected period.",
  [OnDemandStatementStatus.Unavailable]: "Statements are not available for this account.",
  [OnDemandStatementStatus.InvalidRequest]: "Please adjust your selection and try again.",
  [OnDemandStatementStatus.Throttled]: "Please wait a moment before requesting another statement.",
  [OnDemandStatementStatus.Error]: "We couldn't generate your statement. Please try again later.",
};

// Loan-type glyphs, mirroring the dashboard account-widget mapping (account-widget.component.html).
const LOAN_ICONS: Record<string, string> = {
  [ProductCode.LOC]: "data_saver_off",
  [ProductCode.Term]: "assessment",
  [ProductCode.Law]: "work",
};

// A picker option decorated with its display glyph + text (precomputed so the template calls no functions).
interface LoanSelectOption extends StatementLoanOption {
  icon: string;
  display: string;
}

@UntilDestroy()
@Component({
  selector: "ifp-request-statement-dialog",
  templateUrl: "./request-statement-dialog.component.html",
  styleUrls: ["./request-statement-dialog.component.scss"],
  standalone: false,
})
export class RequestStatementDialogComponent implements OnInit {
  form: UntypedFormGroup;

  // Entity mode: a loan picker precedes the period/format fields. Loan mode leaves it off entirely.
  readonly entityMode: boolean;
  loanOptions: LoanSelectOption[] = [];
  selectedLoanOption: LoanSelectOption | null = null;
  pickerAccountOpenedOn?: Date;

  formatOptions: { value: StatementFormat; label: string }[] = [
    { value: "pdf", label: "Pdf" },
    { value: "xlsx", label: "Excel" },
  ];

  submitDisabled = true;
  formatSelected = false;
  generating = false;
  statusMessage = "";
  hasStatusError = false;
  generated: { filename: string } | null = null;

  // The loan the request targets: fixed from data in loan mode, set from the chosen option in entity mode.
  private activeLoanId?: number;
  private activeTemplateId?: number;
  private generatedBlob: Blob | null = null;

  constructor(
    public dialogRef: MatDialogRef<RequestStatementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RequestStatementDialogData,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly loanDocumentService: LoanDocumentService,
    private readonly loanFacade: LoanFacade
  ) {
    this.entityMode = !!this.data.loans?.length;
    this.activeLoanId = this.data.loanId;
    this.activeTemplateId = this.data.templateId;
    this.pickerAccountOpenedOn = this.data.accountOpenedOn;

    this.form = this.formBuilder.group({
      period: [null, Validators.required],
      format: [null, Validators.required],
    });
    if (this.entityMode)
      this.form.addControl("loan", this.formBuilder.control(null, Validators.required));
  }

  static show(
    dialog: MatDialog,
    data: RequestStatementDialogData
  ): Observable<boolean> {
    return dialog
      .open(RequestStatementDialogComponent, {
        // Entity mode carries a third field (the loan picker); give it the wider Figma layout.
        width: data.loans?.length ? "580px" : "480px",
        panelClass: "statement-dialog-panel",
        data,
        closeOnNavigation: true,
        autoFocus: false,
      })
      .afterClosed();
  }

  ngOnInit(): void {
    this.loanOptions = (this.data.loans ?? []).map((loan) => ({
      ...loan,
      icon: LOAN_ICONS[loan.productCode] ?? "request_quote",
      display: `${loan.label} *${loan.lastFour}`,
    }));

    this.submitDisabled = this.form.invalid;
    this.form.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => (this.submitDisabled = this.form.invalid));

    this.form
      .get("period")
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this.clearStatus());

    this.form
      .get("format")
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => (this.formatSelected = value != null));

    if (this.entityMode)
      this.form
        .get("loan")
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe((option: LoanSelectOption) => this.onLoanSelected(option));
  }

  generate(): void {
    if (this.form.invalid || this.activeLoanId == null || this.activeTemplateId == null) {
      FormHelper.showInvalidFormFields(this.form);
      return;
    }

    this.clearStatus();
    const value = this.form.getRawValue();
    const period: StatementPeriodSelection = value.period;
    const format: StatementFormat = value.format;
    const request: OnDemandStatementRequest =
      period.granularity === "month"
        ? { granularity: "month", year: period.year, month: period.month, format }
        : { granularity: "year", year: period.year, format };

    this.generating = true;
    this.loanDocumentService
      .downloadOnDemandStatement(this.activeLoanId, this.activeTemplateId, request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result: StatementDownloadResult) => {
          this.generating = false;
          if (result.kind === "file") {
            this.showGenerated(result.blob, result.filename);
            return;
          }
          this.showStatus(result.envelope);
        },
        error: () => {
          this.generating = false;
          this.showStatus({
            status: OnDemandStatementStatus.Error,
            message: STATUS_FALLBACK_MESSAGES[OnDemandStatementStatus.Error],
          });
        },
      });
  }

  downloadFile(): void {
    if (this.generatedBlob != null && this.generated != null)
      this.loanDocumentService.downloadStatementFile(this.generatedBlob, this.generated.filename);
  }

  trackByFormat(_index: number, option: { value: StatementFormat }): StatementFormat {
    return option.value;
  }

  trackByLoanId(_index: number, option: LoanSelectOption): number {
    return option.loanId;
  }

  // A new loan targets a different account: retarget the request, drop the stale period, and re-floor the
  // picker at the chosen loan's chain origin (the option rarely carries it, so fetch the loan when it doesn't).
  private onLoanSelected(option: LoanSelectOption | null): void {
    this.selectedLoanOption = option ?? null;
    if (option == null)
      return;

    this.activeLoanId = option.loanId;
    this.activeTemplateId = option.templateId;
    this.form.get("period").setValue(null);

    if (option.accountOpenedOn != null) {
      this.pickerAccountOpenedOn = option.accountOpenedOn;
      return;
    }
    this.loanFacade
      .getLoan$(option.loanId)
      .pipe(take(1), untilDestroyed(this))
      .subscribe((loan: LoanData) => {
        this.pickerAccountOpenedOn = loan?.chainOriginatedOn ?? loan?.createdOn;
      });
  }

  private showGenerated(blob: Blob, filename: string): void {
    this.generatedBlob = blob;
    this.generated = { filename };
  }

  // No-leak: surface only the generic message/fallback — never a suppression reason.
  private showStatus(envelope: OnDemandStatementEnvelope): void {
    this.statusMessage =
      envelope.message && envelope.message.trim().length > 0
        ? envelope.message
        : STATUS_FALLBACK_MESSAGES[envelope.status] ?? STATUS_FALLBACK_MESSAGES[OnDemandStatementStatus.Error];
    this.hasStatusError = true;
  }

  private clearStatus(): void {
    this.statusMessage = "";
    this.hasStatusError = false;
  }
}
